import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button, ScrollArea, Separator, Tabs, TabsContent, TabsList, TabsTrigger, cn } from 'shared-ui';

import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { PoiPanelField } from './PoiPanelFields';
import { StatusPill } from '@/components/shared/StatusPill';
import { getVanStatePillPresentation } from '@/components/shared/statusPillPresentation';
import { useVehicleDetails } from '@/hooks/useVehicleDetails';
import type { DisplayedTripTrack } from '@/hooks/useVehicleTripRouteLayer';
import type { VanFeatureProperties } from '../utils/vanFeatureProperties';
import { vanPanelTitle } from '../utils/vanFeatureProperties';
import { formatLondonDateTime } from '../utils/vanTripFormat';
import { VanTripsTab } from './VanTripsTab';

interface VanDetailsPanelProps {
    van: VanFeatureProperties | null;
    isOpen: boolean;
    onClose: () => void;
    /** Fired when the details request 403s — the per-user capability was revoked mid-session. */
    onForbidden?: () => void;
    /** Hands a fetched trip track up for the map to draw. */
    onShowTripTrack?: (displayed: DisplayedTripTrack) => void;
}

// Europe/London explicitly, like the Trips tab — never the browser's locale or zone
function formatInstant(iso: string | null | undefined): string {
    return formatLondonDateTime(iso) ?? 'Unavailable';
}

// The details-panel form idiom (PoiPanelField's bordered read-only control), matching
// the POI/leak panels and the history detail: timestamps carry the calendar glyph,
// categorised values the dropdown chevron, free values a plain input. "Unavailable"
// dims like the panels' unset-date placeholders.
function PanelField({ label, value, kind }: { label: string; value: string; kind?: 'choice' | 'date' }) {
    return (
        <PoiPanelField label={label}>
            <span className={cn('flex-1 text-sm', value === 'Unavailable' && 'text-muted-foreground')}>{value}</span>
            {kind === 'date' && <Calendar className="poi-panel-control__trailing size-4" aria-hidden="true" />}
            {kind === 'choice' && <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />}
        </PoiPanelField>
    );
}

function PanelContent({
    van,
    isOpen,
    onClose,
    onForbidden,
    onShowTripTrack,
}: { van: VanFeatureProperties; isOpen: boolean; onClose: () => void } & Pick<
    VanDetailsPanelProps,
    'onForbidden' | 'onShowTripTrack'
>) {
    // Gated on isOpen: SlidingPanel keeps children mounted while closed (for the slide-out
    // transition), and a reopen must fetch fresh — live vehicle data goes stale in seconds.
    const { details, isLoading, isError, retry } = useVehicleDetails(van.id, isOpen, onForbidden);

    // Controlled tabs with the lazy-trips flag: no trip request fires until the user first
    // opens the Trips tab for this van (the PoiDetailsPanel history idiom). Selecting a
    // different van resets both, derived at render time.
    const [activeTab, setActiveTab] = useState('details');
    const [hasActivatedTrips, setHasActivatedTrips] = useState(false);
    const [prevVanId, setPrevVanId] = useState(van.id);
    if (van.id !== prevVanId) {
        setPrevVanId(van.id);
        setHasActivatedTrips(activeTab === 'trips');
    }

    const title = vanPanelTitle(van);

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* The title is the make/model only — the plate the provider folds into the
                display name is stripped, since it has its own Registration field below. */}
            <div className="flex items-start justify-between px-6 pt-6 pb-3">
                <div>
                    {title && <h2 className="text-xl font-black uppercase">{title}</h2>}
                    <StatusPill presentation={getVanStatePillPresentation(van.state)} className={cn(title && 'mt-2')} />
                </div>
                <PanelCloseButton onClick={onClose} aria-label="Close van details" />
            </div>
            <Separator />
            <Tabs
                value={activeTab}
                onValueChange={(value) => {
                    setActiveTab(String(value));
                    if (value === 'trips') setHasActivatedTrips(true);
                }}
                className="flex min-h-0 flex-1 flex-col"
            >
                <TabsList variant="line" className="poi-panel-tabs-list w-full rounded-none border-b p-0">
                    <TabsTrigger value="details" className="text-primary data-active:text-primary flex-1">
                        Details
                    </TabsTrigger>
                    <TabsTrigger value="trips" className="text-primary data-active:text-primary flex-1">
                        Trips
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-0 flex min-h-0 flex-1 flex-col">
                    <ScrollArea className="min-h-0 flex-1">
                        <div className="space-y-4 px-6 py-4">
                            {isLoading && (
                                <div role="status" aria-label="Loading vehicle details" className="space-y-4">
                                    <div className="bg-muted h-10 w-full animate-pulse rounded" />
                                    <div className="bg-muted h-10 w-full animate-pulse rounded" />
                                    <div className="bg-muted h-10 w-2/3 animate-pulse rounded" />
                                </div>
                            )}
                            {isError && (
                                <div className="space-y-2">
                                    <p className="text-destructive text-sm">
                                        Unable to load vehicle details — please try again.
                                    </p>
                                    <Button variant="outline" size="sm" onClick={retry}>
                                        Try Again
                                    </Button>
                                </div>
                            )}
                            {details && (
                                <>
                                    <PanelField
                                        label="Operative"
                                        value={details.operativeName ?? 'Unknown operative'}
                                    />
                                    <PanelField label="Registration" value={details.registration ?? 'Unavailable'} />
                                    <PanelField
                                        label="Vehicle Type"
                                        value={details.vehicleType ?? 'Unavailable'}
                                        kind="choice"
                                    />
                                    {/* The unit rides inside the control, the panels' Leakage
                                        Rate "Ml/D" idiom. */}
                                    <PoiPanelField label="Speed">
                                        <span className="poi-panel-control__unit">km/h</span>
                                        <Separator orientation="vertical" aria-hidden="true" />
                                        <span
                                            className={cn(
                                                'flex-1 text-sm',
                                                details.speedKph == null && 'text-muted-foreground',
                                            )}
                                        >
                                            {details.speedKph != null ? String(details.speedKph) : 'Unavailable'}
                                        </span>
                                    </PoiPanelField>
                                    <PanelField
                                        label="Position Reported"
                                        value={formatInstant(details.positionTime)}
                                        kind="date"
                                    />
                                    <PanelField
                                        label="Last Updated"
                                        value={formatInstant(details.lastUpdated)}
                                        kind="date"
                                    />
                                    {!details.isActive && <PanelField label="Status" value="Retired from the fleet" />}
                                </>
                            )}
                        </div>
                    </ScrollArea>
                </TabsContent>

                <TabsContent value="trips" className="mt-0 flex min-h-0 flex-1 flex-col">
                    <VanTripsTab
                        vehicleId={van.id}
                        enabled={hasActivatedTrips && isOpen}
                        onForbidden={onForbidden}
                        onShowTrack={onShowTripTrack}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}

/**
 * Van details side panel (the PoiDetailsPanel pattern): the sticky last-van idiom keeps
 * content rendered during the close transition, and details are fetched live per open —
 * vehicle position/state would only mislead if replayed from a cache.
 */
export function VanDetailsPanel({ van, isOpen, onClose, onForbidden, onShowTripTrack }: VanDetailsPanelProps) {
    const [lastVan, setLastVan] = useState<VanFeatureProperties | null>(van ?? null);
    const [prevVan, setPrevVan] = useState(van);
    if (van !== prevVan) {
        setPrevVan(van);
        if (van !== null) setLastVan(van);
    }

    return (
        <SlidingPanel isOpen={isOpen} onClose={onClose} ariaLabel="Van details" title="Van Details">
            {lastVan && (
                <PanelContent
                    van={lastVan}
                    isOpen={isOpen}
                    onClose={onClose}
                    onForbidden={onForbidden}
                    onShowTripTrack={onShowTripTrack}
                />
            )}
        </SlidingPanel>
    );
}
